import { Button } from '@crowdex/ui/components/button';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { FIND_IN_CONVERSATION_EVENT } from '@/components/desktop-shell/conversation-menu';

const ALL = 'crowdex-find';
const CURRENT = 'crowdex-find-current';

/** The ranges of `query` in the messages under `root` (case-insensitive), in reading order. */
export function findInMessages(root: Element, query: string): Range[] {
  const needle = query.toLowerCase();
  if (needle.length === 0) return [];
  const ranges: Range[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest('.desktop-message')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  });
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const text = (node.textContent ?? '').toLowerCase();
    for (let at = text.indexOf(needle); at !== -1; at = text.indexOf(needle, at + needle.length)) {
      const range = document.createRange();
      range.setStart(node, at);
      range.setEnd(node, at + needle.length);
      ranges.push(range);
    }
  }
  return ranges;
}

type Highlights = { set: (name: string, value: unknown) => void; delete: (name: string) => void };

function highlights(): Highlights | null {
  const registry = (globalThis.CSS as { highlights?: Highlights } | undefined)?.highlights;
  const Highlight = (globalThis as { Highlight?: unknown }).Highlight;
  return registry !== undefined && typeof Highlight === 'function' ? registry : null;
}

function paint(ranges: readonly Range[], current: number) {
  const registry = highlights();
  if (registry === null) return;
  const Highlight = (globalThis as unknown as { Highlight: new (...ranges: Range[]) => unknown })
    .Highlight;
  registry.set(ALL, new Highlight(...ranges));
  const focused = ranges[current];
  if (focused === undefined) registry.delete(CURRENT);
  else registry.set(CURRENT, new Highlight(focused));
}

function clear() {
  highlights()?.delete(ALL);
  highlights()?.delete(CURRENT);
}

/**
 * Find in conversation (D6 ⌘F): a bar over the transcript, below the header (`belowHeader`), that
 * marks every match in the messages and goes from one to the next (↵, ⇧↵). Esc closes it from
 * anywhere in the pane. The marks' colours are `::highlight(crowdex-find…)` in globals.css.
 */
export function ConversationFind({
  root,
  belowHeader = false,
}: {
  root: RefObject<HTMLElement | null>;
  belowHeader?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [current, setCurrent] = useState(0);
  // The matches of the query: a new search repaints them even when it finds as many.
  const [ranges, setRanges] = useState<readonly Range[]>([]);
  const count = ranges.length;
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const show = () => {
      setOpen(true);
      // Already open: back to its field, the query selected.
      input.current?.focus();
      input.current?.select();
    };
    window.addEventListener(FIND_IN_CONVERSATION_EVENT, show);
    return () => window.removeEventListener(FIND_IN_CONVERSATION_EVENT, show);
  }, []);

  useEffect(() => {
    if (!open || root.current === null) {
      clear();
      return;
    }
    setRanges(findInMessages(root.current, query));
    setCurrent(0);
  }, [open, query, root]);

  useEffect(() => {
    if (!open) return;
    paint(ranges, current);
    ranges[current]?.startContainer.parentElement?.scrollIntoView?.({ block: 'center' });
  }, [open, current, ranges]);

  // Opened, the field takes the focus (the menu or ⌘F left it on the page).
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    input.current?.select();
  }, [open]);

  // Esc closes it from anywhere in the pane (or with nothing focused), not from the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target;
      const inPane =
        target === document.body ||
        target === document.documentElement ||
        (target instanceof Node && root.current?.contains(target) === true);
      if (!inPane) return;
      event.preventDefault();
      setOpen(false);
      setQuery('');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, root]);

  useEffect(() => clear, []);

  if (!open) return null;
  const move = (delta: 1 | -1) =>
    setCurrent((index) => (count === 0 ? 0 : (index + delta + count) % count));
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  return (
    <search
      aria-label="Find in conversation"
      // Centred over the transcript's column (it moves aside for a pinned Summary), clear of the
      // header's controls and of the Summary.
      className={`absolute left-1/2 ${belowHeader ? 'top-16' : 'top-2'} z-30 flex items-center gap-1 rounded-lg border border-hairline-strong bg-popover py-1 pr-1 pl-3 shadow-desktop-float`}
      style={{ translate: 'calc(-50% + var(--thread-content-shift, 0px)) 0' }}
    >
      <Search className="size-3.5 shrink-0 text-muted-foreground" />
      <input
        aria-label="Find in conversation"
        className="type-ui w-44 bg-transparent outline-none placeholder:text-muted-foreground"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            move(event.shiftKey ? -1 : 1);
          }
        }}
        placeholder="Find in conversation"
        ref={input}
        value={query}
      />
      <span className="type-caption min-w-10 text-center text-muted-foreground tabular-nums">
        {query.length === 0 ? '' : count === 0 ? '0/0' : `${current + 1}/${count}`}
      </span>
      <Button aria-label="Previous match" onClick={() => move(-1)} size="icon-sm" variant="ghost">
        <ChevronUp className="size-3.5" />
      </Button>
      <Button aria-label="Next match" onClick={() => move(1)} size="icon-sm" variant="ghost">
        <ChevronDown className="size-3.5" />
      </Button>
      <Button aria-label="Close find" onClick={close} size="icon-sm" variant="ghost">
        <X className="size-3.5" />
      </Button>
    </search>
  );
}
